import { type ReactNode } from 'react';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List as ListIcon,
  ListOrdered,
  Quote,
  Code,
  Link as LinkIcon,
  Image as ImageIcon,
  Minus,
  Undo2,
  Redo2,
  Sparkles,
} from 'lucide-react';
import { Btn, IconBtn } from './ui';

export type FormatCommand =
  | 'undo'
  | 'redo'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strike'
  | 'bulletList'
  | 'orderedList'
  | 'blockquote'
  | 'code'
  | 'link'
  | 'image'
  | 'divider';

export interface FormatToolbarProps {
  onFormat?: (cmd: FormatCommand) => void;
  activeFormats?: Partial<Record<FormatCommand, boolean>>;
  onAskAI?: () => void;
}

interface ToolbarBtn {
  cmd: FormatCommand;
  icon: ReactNode;
  label: string;
}

const TOOLBAR_GROUPS: ToolbarBtn[][] = [
  [
    { cmd: 'undo', icon: <Undo2 className="size-4" />, label: 'Undo' },
    { cmd: 'redo', icon: <Redo2 className="size-4" />, label: 'Redo' },
  ],
  [
    { cmd: 'h1', icon: <Heading1 className="size-4" />, label: 'H1' },
    { cmd: 'h2', icon: <Heading2 className="size-4" />, label: 'H2' },
    { cmd: 'h3', icon: <Heading3 className="size-4" />, label: 'H3' },
  ],
  [
    { cmd: 'bold', icon: <BoldIcon className="size-4" />, label: 'Bold' },
    { cmd: 'italic', icon: <ItalicIcon className="size-4" />, label: 'Italic' },
    { cmd: 'underline', icon: <UnderlineIcon className="size-4" />, label: 'Underline' },
    { cmd: 'strike', icon: <Strikethrough className="size-4" />, label: 'Strikethrough' },
  ],
  [
    { cmd: 'bulletList', icon: <ListIcon className="size-4" />, label: 'Bullet list' },
    { cmd: 'orderedList', icon: <ListOrdered className="size-4" />, label: 'Numbered list' },
    { cmd: 'blockquote', icon: <Quote className="size-4" />, label: 'Quote' },
    { cmd: 'code', icon: <Code className="size-4" />, label: 'Code' },
  ],
  [
    { cmd: 'link', icon: <LinkIcon className="size-4" />, label: 'Link' },
    { cmd: 'image', icon: <ImageIcon className="size-4" />, label: 'Image' },
    { cmd: 'divider', icon: <Minus className="size-4" />, label: 'Divider' },
  ],
];

export function FormatToolbar({ onFormat, activeFormats, onAskAI }: FormatToolbarProps) {
  return (
    <div
      data-component="format-toolbar"
      className="flex items-center gap-1.5 px-2.5 py-1 min-h-9"
    >
      {TOOLBAR_GROUPS.map((grp, i) => (
        <div key={i} className="flex items-center gap-0.5">
          {i > 0 && <div className="mx-1 h-4 w-px bg-border" />}
          {grp.map((b) => (
            <IconBtn
              key={b.cmd}
              icon={b.icon}
              label={b.label}
              size={28}
              active={activeFormats?.[b.cmd]}
              onClick={() => onFormat?.(b.cmd)}
              disabled={!onFormat}
            />
          ))}
        </div>
      ))}
      <div className="flex-1" />
      {onAskAI && (
        <Btn
          variant="bare"
          size="sm"
          icon={<Sparkles className="size-3.5 text-coral" />}
          onClick={onAskAI}
        >
          Ask AI
        </Btn>
      )}
    </div>
  );
}
